import { getAccessToken, getCurrentUser } from './authApi';

const API_BASE = (process.env.REACT_APP_API_BASE_URL || '/api').replace(/\/$/, '');
const TACO_SEARCH_URL = (process.env.REACT_APP_TACO_API_URL || `${API_BASE}/taco-search.php`).replace(/\/$/, '');

export class FoodDatabaseUnavailableError extends Error {
  constructor() {
    super('Não foi possível consultar a tabela de composição de alimentos. Verifique a conexão e tente novamente.');
    this.name = 'FoodDatabaseUnavailableError';
  }
}

function toNumber(value: unknown): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function toOptionalNumber(value: unknown): number | undefined {
  if (value === null || value === undefined || value === '') return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function normalizeSearchText(value: string): string {
  return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

export interface FoodItem {
  id: string;
  name: string;
  brand?: string;
  barcode?: string;
  category: 'protein' | 'carbs' | 'fats' | 'vegetables' | 'fruits' | 'dairy' | 'grains' | 'snacks' | 'beverages' | 'other';
  servingSize: number;
  servingUnit: 'g' | 'ml' | 'unit' | 'cup' | 'tablespoon' | 'teaspoon';
  nutritionalInfo: {
    calories: number;
    protein: number; // gramas
    carbs: number; // gramas
    fats: number; // gramas
    fiber?: number; // gramas
    sugar?: number; // gramas
    sodium?: number; // miligramas
    // Campos adicionais da TACO (quando disponíveis)
    calcium?: number; // miligramas
    magnesium?: number; // miligramas
    phosphorus?: number; // miligramas
    iron?: number; // miligramas
    potassium?: number; // miligramas
    zinc?: number; // miligramas
    copper?: number; // miligramas
    manganese?: number; // miligramas
    vitaminA?: number; // mcg RAE
    vitaminC?: number; // miligramas
    vitaminD?: number; // mcg
    vitaminE?: number; // miligramas
    vitaminK?: number; // mcg
    thiamin?: number; // miligramas (B1)
    riboflavin?: number; // miligramas (B2)
    niacin?: number; // miligramas (B3)
    vitaminB6?: number; // miligramas
    folate?: number; // mcg
    vitaminB12?: number; // mcg
    cholesterol?: number; // miligramas
    saturatedFat?: number; // gramas
    monounsaturatedFat?: number; // gramas
    polyunsaturatedFat?: number; // gramas
    transFat?: number; // gramas
  };
  verified: boolean;
  testFixture?: boolean;
  customFood: boolean;
  fromTaco?: boolean; // Indica se vem da tabela TACO
  tacoId?: number; // ID no banco TACO
  codigoTaco?: string; // Código TACO original
  nomeCientifico?: string; // Nome científico do alimento
  createdBy?: string; // nutritionistId ou patientId
  tags?: string[];
  createdAt: string;
  updatedAt?: string;
}

async function customFoodRequest(action: string, method: 'GET' | 'POST', body?: Record<string, unknown>): Promise<any> {
  const token = getAccessToken();
  if (!token) throw new Error('Entre novamente para acessar seus alimentos personalizados.');
  const response = await fetch(`${API_BASE}/custom-foods.php?action=${encodeURIComponent(action)}`, {
    method,
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}`, ...(method === 'POST' ? { 'Content-Type': 'application/json' } : {}) },
    body: method === 'POST' ? JSON.stringify(body || {}) : undefined,
    cache: 'no-store',
  });
  const result = await response.json().catch(() => null);
  if (!response.ok || !result?.success) throw new Error(result?.error || 'Não foi possível acessar os alimentos personalizados.');
  return result.data;
}

/**
 * Interface para alimento TACO retornado pela API
 */
interface TacoFoodResponse {
  testFixture?: boolean;
  id: number;
  codigo_taco?: string;
  nome: string;
  nome_cientifico?: string;
  categoria?: string;
  grupo?: string;
  energia_kcal?: number;
  proteina_g?: number;
  carboidrato_g?: number;
  lipideos_g?: number;
  fibra_alimentar_g?: number;
  sodio_mg?: number;
  calcio_mg?: number;
  magnesio_mg?: number;
  fosforo_mg?: number;
  ferro_mg?: number;
  potassio_mg?: number;
  zinco_mg?: number;
  cobre_mg?: number;
  manganes_mg?: number;
  vitamina_c_mg?: number;
  vitamina_d_mcg?: number;
  vitamina_e_mg?: number;
  vitamina_k_mcg?: number;
  tiamina_mg?: number;
  riboflavina_mg?: number;
  niacina_mg?: number;
  piridoxina_mg?: number;
  folato_mcg?: number;
  vitamina_b12_mcg?: number;
  rae_mcg?: number;
  colesterol_mg?: number;
  acido_graxo_saturado_g?: number;
  acido_graxo_monoinsaturado_g?: number;
  acido_graxo_poliinsaturado_g?: number;
  acido_graxo_trans_g?: number;
  porcao_g?: number;
}

/**
 * Mapeia alimento TACO para formato FoodItem
 */
function mapTacoToFoodItem(tacoFood: TacoFoodResponse): FoodItem {
  const categoryText = normalizeSearchText(`${tacoFood.categoria || ''} ${tacoFood.grupo || ''}`);
  let category: FoodItem['category'] = 'other';
  if (/leite|queijo|laticin/.test(categoryText)) category = 'dairy';
  else if (/fruta/.test(categoryText)) category = 'fruits';
  else if (/carn|ave|peixe|pescad|ovo|legumin/.test(categoryText)) category = 'protein';
  else if (/verdura|legume|hortal/.test(categoryText)) category = 'vegetables';
  else if (/cereal|pao|massa|tuberculo|raiz/.test(categoryText)) category = 'carbs';
  else if (/oleo|gordura|nozes|semente/.test(categoryText)) category = 'fats';
  else if (/bebida/.test(categoryText)) category = 'beverages';

  // Os nutrientes da tabela são por 100g; porcao_g pode ser uma medida caseira.
  const porcao = 100;

  return {
    id: `taco_${tacoFood.id}`,
    name: tacoFood.nome,
    category,
    servingSize: porcao,
    servingUnit: 'g',
    nutritionalInfo: {
      calories: toNumber(tacoFood.energia_kcal),
      protein: toNumber(tacoFood.proteina_g),
      carbs: toNumber(tacoFood.carboidrato_g),
      fats: toNumber(tacoFood.lipideos_g),
      fiber: toOptionalNumber(tacoFood.fibra_alimentar_g),
      sodium: toOptionalNumber(tacoFood.sodio_mg),
      calcium: toOptionalNumber(tacoFood.calcio_mg),
      magnesium: toOptionalNumber(tacoFood.magnesio_mg),
      phosphorus: toOptionalNumber(tacoFood.fosforo_mg),
      iron: toOptionalNumber(tacoFood.ferro_mg),
      potassium: toOptionalNumber(tacoFood.potassio_mg),
      zinc: toOptionalNumber(tacoFood.zinco_mg),
      copper: toOptionalNumber(tacoFood.cobre_mg),
      manganese: toOptionalNumber(tacoFood.manganes_mg),
      vitaminA: toOptionalNumber(tacoFood.rae_mcg),
      vitaminC: toOptionalNumber(tacoFood.vitamina_c_mg),
      vitaminD: toOptionalNumber(tacoFood.vitamina_d_mcg),
      vitaminE: toOptionalNumber(tacoFood.vitamina_e_mg),
      vitaminK: toOptionalNumber(tacoFood.vitamina_k_mcg),
      thiamin: toOptionalNumber(tacoFood.tiamina_mg),
      riboflavin: toOptionalNumber(tacoFood.riboflavina_mg),
      niacin: toOptionalNumber(tacoFood.niacina_mg),
      vitaminB6: toOptionalNumber(tacoFood.piridoxina_mg),
      folate: toOptionalNumber(tacoFood.folato_mcg),
      vitaminB12: toOptionalNumber(tacoFood.vitamina_b12_mcg),
      cholesterol: toOptionalNumber(tacoFood.colesterol_mg),
      saturatedFat: toOptionalNumber(tacoFood.acido_graxo_saturado_g),
      monounsaturatedFat: toOptionalNumber(tacoFood.acido_graxo_monoinsaturado_g),
      polyunsaturatedFat: toOptionalNumber(tacoFood.acido_graxo_poliinsaturado_g),
      transFat: toOptionalNumber(tacoFood.acido_graxo_trans_g),
    },
    verified: !tacoFood.testFixture,
    testFixture: tacoFood.testFixture,
    customFood: false,
    fromTaco: !tacoFood.testFixture,
    tacoId: tacoFood.id,
    codigoTaco: tacoFood.codigo_taco,
    nomeCientifico: tacoFood.nome_cientifico,
    tags: tacoFood.categoria ? [tacoFood.categoria.toLowerCase()] : [],
    createdAt: new Date().toISOString(),
  };
}

// ------ Funções de Busca ------

async function searchTacoFoods(searchTerm?: string, category?: string, limit: number = 50): Promise<FoodItem[]> {
  const categoryFilters: Record<string, string[]> = {
    protein: ['carnes', 'aves', 'peixes', 'pescados', 'ovos', 'leguminosas'],
    grains: ['cereais'],
    carbs: ['cereais', 'massas', 'pães', 'tubérculos', 'leguminosas'],
    fats: ['óleos', 'gorduras', 'nozes', 'sementes'],
    vegetables: ['verduras', 'legumes'],
    fruits: ['frutas'],
    dairy: ['leites', 'queijos'],
    beverages: ['bebidas'],
  };
  const filters = category ? (categoryFilters[category] || [category]) : [''];
  const safeLimit = Math.min(Math.max(Math.trunc(limit) || 50, 1), 100);

  const requests = filters.map(async (categoryFilter) => {
    const params = new URLSearchParams();
    const term = searchTerm?.trim();
    if (term) params.append('term', term);
    if (categoryFilter) params.append('category', categoryFilter);
    params.append('limit', String(safeLimit));

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 12000);
    try {
      const response = await fetch(`${TACO_SEARCH_URL}?${params.toString()}`, { signal: controller.signal });
      if (!response.ok) throw new Error(`API TACO respondeu ${response.status}`);

      const result = await response.json();
      if (!result?.success || !Array.isArray(result.data)) {
        throw new Error('Formato inválido retornado pela API TACO');
      }
      return result.data as TacoFoodResponse[];
    } finally {
      clearTimeout(timeout);
    }
  });

  const settled = await Promise.allSettled(requests);
  const successful = settled.filter((result): result is PromiseFulfilledResult<TacoFoodResponse[]> => result.status === 'fulfilled');
  if (successful.length === 0) {
    console.error('Erro ao buscar alimentos TACO:', settled.find(result => result.status === 'rejected'));
    throw new FoodDatabaseUnavailableError();
  }

  const uniqueFoods = new Map<string, FoodItem>();
  successful.forEach(({ value }) => value.forEach((food) => {
    const mapped = mapTacoToFoodItem(food);
    uniqueFoods.set(mapped.id, mapped);
  }));
  if (uniqueFoods.size === 0 && successful.length < settled.length) {
    throw new FoodDatabaseUnavailableError();
  }
  return Array.from(uniqueFoods.values())
    .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
    .slice(0, safeLimit);
}

async function searchCustomFoodsMySQL(userId?: string): Promise<FoodItem[]> {
  if (!userId) return [];
  try {
    const foods = await customFoodRequest('list', 'GET');
    return Array.isArray(foods) ? foods as FoodItem[] : [];
  } catch (error) {
    console.error('Erro ao buscar alimentos customizados MySQL:', error);
    return [];
  }
}

/**
 * Busca alimentos no banco de dados (TACO + Custom MySQL)
 */
export async function searchFoods(
  searchTerm?: string,
  category?: FoodItem['category'],
  limit: number = 50
): Promise<FoodItem[]> {
  const allFoods: FoodItem[] = [];
  let tacoSearchError: unknown;

  try {
    // A API aceita termo e categoria vazios para devolver alimentos padrão.
    try {
      const tacoFoods = await searchTacoFoods(searchTerm, category, limit);
      allFoods.push(...tacoFoods);
    } catch (error) {
      tacoSearchError = error;
    }

    // 2. Custom Foods (MySQL)
    const user = getCurrentUser();
    if (user) {
      let customFoods = await searchCustomFoodsMySQL(user.uid);

      // Filtragem local dos custom foods (idealmente seria no backend, mas mantendo simples)
      if (searchTerm) {
        const term = normalizeSearchText(searchTerm.trim());
        customFoods = customFoods.filter(f => normalizeSearchText(f.name).includes(term));
      }
      if (category) {
        customFoods = customFoods.filter(f => f.category === category);
      }

      allFoods.push(...customFoods);
    }

  } catch (error) {
    console.error('Erro ao buscar alimentos:', error);
  }

  if (allFoods.length === 0 && tacoSearchError) {
    throw tacoSearchError;
  }

  // Ordenação: TACO primeiro? Não, Custom pode ser prioridade.
  // Vamos manter TACO separado de Custom se for misturado, ou ordenado por nome.
  // Regra atual: Custom foods primeiro, depois TACO, ordenados por nome.

  return allFoods.sort((a, b) => {
    // Custom foods check
    if (a.customFood && !b.customFood) return -1;
    if (!a.customFood && b.customFood) return 1;
    return a.name.localeCompare(b.name);
  });
}

export async function getFoodById(foodId: string): Promise<FoodItem | null> {
  try {
    if (foodId.startsWith('taco_')) {
      const tacoId = parseInt(foodId.replace('taco_', ''), 10);
      const url = `${TACO_SEARCH_URL}?id=${tacoId}`;
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`API TACO falhou: ${response.status}`);
      }

      const result = await response.json();
      if (result.success && result.data) {
        return mapTacoToFoodItem(result.data);
      }
    } else {
      // Custom Food: Buscar na lista do usuário
      const user = getCurrentUser();
      if (user) {
        const customFoods = await searchCustomFoodsMySQL(user.uid);
        const found = customFoods.find(f => f.id === foodId);
        return found || null;
      }
    }
    return null;
  } catch (error) {
    console.error('Erro ao buscar alimento:', error);
    return null;
  }
}

// ------ Operações de Escrita (MySQL) ------

export async function createCustomFood(
  userId: string,
  food: Omit<FoodItem, 'id' | 'createdAt' | 'customFood' | 'verified'>
): Promise<string> {
  try {
    const result = await customFoodRequest('create', 'POST', { ...food, userId });
    if (result?.id) return String(result.id);
    throw new Error('O servidor não retornou o identificador do alimento.');
  } catch (error) {
    console.error('Erro ao criar alimento personalizado:', error);
    throw new Error('Não foi possível criar o alimento');
  }
}

export async function updateFood(
  foodId: string,
  updates: Partial<FoodItem>
): Promise<void> {
  try {
    await customFoodRequest('update', 'POST', { foodId, updates });
  } catch (error) {
    console.error('Erro ao atualizar alimento:', error);
    throw new Error('Não foi possível atualizar o alimento');
  }
}

export async function deleteFood(foodId: string, userId: string): Promise<void> {
  try {
    if (foodId.startsWith('taco_')) {
      throw new Error('Não é possível deletar alimentos TACO');
    }

    await customFoodRequest('delete', 'POST', { foodId, userId });
  } catch (error) {
    console.error('Erro ao deletar alimento:', error);
    throw new Error('Não foi possível deletar o alimento');
  }
}

export function calculateNutritionalValues(
  food: FoodItem,
  quantity: number,
  unit: FoodItem['servingUnit']
): FoodItem['nutritionalInfo'] {
  let multiplier = 1;
  if (unit !== food.servingUnit) {
    if (food.servingUnit === 'g' && unit === 'ml') multiplier = quantity / food.servingSize;
    else if (food.servingUnit === 'ml' && unit === 'g') multiplier = quantity / food.servingSize;
    else multiplier = quantity / food.servingSize; // Simplificação
  } else {
    multiplier = quantity / food.servingSize;
  }

  return {
    calories: Math.round(food.nutritionalInfo.calories * multiplier),
    protein: Math.round(food.nutritionalInfo.protein * multiplier * 10) / 10,
    carbs: Math.round(food.nutritionalInfo.carbs * multiplier * 10) / 10,
    fats: Math.round(food.nutritionalInfo.fats * multiplier * 10) / 10,
    fiber: food.nutritionalInfo.fiber ? Math.round(food.nutritionalInfo.fiber * multiplier * 10) / 10 : undefined,
    sugar: food.nutritionalInfo.sugar ? Math.round(food.nutritionalInfo.sugar * multiplier * 10) / 10 : undefined,
    sodium: food.nutritionalInfo.sodium ? Math.round(food.nutritionalInfo.sodium * multiplier) : undefined,
  };
}

/**
 * Inicialização (Verificação da tabela)
 */
export async function initializeFoodDatabase(): Promise<void> {
  // A camada PHP/MySQL é provisionada pela migration do servidor.
}
